import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Plus, Minus, Calendar, Upload, ClipboardList } from 'lucide-react';
import { DatePicker } from '../ui/date-picker';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

type Assignment = {
  id: string;
  title: string;
  description?: string;
  dueDate?: Date;
  file?: File;
  fileLink?: string;
  link?:string;
}

interface AssignmentsListProps {
  tasks: Assignment[];
  onAssignmentsChange: (tasks: Assignment[]) => void;
}

const AssignmentsList = ({ tasks, onAssignmentsChange }: AssignmentsListProps) => {
  const fileInputRefs = useState<{[key: string]: HTMLInputElement | null}>({});
  
  const addAssignment = () => {
    const newAssignment: any = {
      id: Date.now().toString(),
      title: '',
      link: '',
      fileLink:''
    };
    
    onAssignmentsChange([...tasks, newAssignment]);
  };
  
  const removeAssignment = (id: string) => {
    onAssignmentsChange(tasks?.filter(assignment => assignment.id !== id));
  };
  
  const updateAssignment = (id: string, updates: Partial<Assignment>) => {
    onAssignmentsChange(
      tasks?.map(assignment => 
        assignment.id === id ? { ...assignment, ...updates } : assignment
      )
    );
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, assignmentId: string) => {
    const file = e.target.files?.[0];
    if (file) {
      updateAssignment(assignmentId, { 
        file,
        fileLink: '' // Clear link when file is uploaded
      });
    }
  };
  
  return (
    <div className="space-y-4 border rounded-lg p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-bytesplash-blue">Assignments</h3>
        <Button 
          type="button" 
          variant="outline" 
          size="sm" 
          onClick={addAssignment}
        >
          <Plus className="mr-1 h-4 w-4" />
          Add Assignment
        </Button>
      </div>
      
      {tasks?.map((assignment, index) => (
        <div key={assignment.id} className="space-y-4 border-b pb-4 last:border-0">
          <div className="flex items-center gap-2">
            <span className="text-bytesplash-blue font-medium">
              Assignment {index + 1}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
              onClick={() => removeAssignment(assignment.id)}
            >
              <Minus className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`assignment-title-${assignment.id}`}>
                Assignment Title<span className="text-red-500">*</span>
              </Label>
              <Input
                id={`assignment-title-${assignment.id}`}
                value={assignment.title}
                onChange={(e) => updateAssignment(assignment.id, { title: e.target.value })}
                placeholder="e.g., HTML Structure Exercise"
              />
            </div>
            
            {/* <div className="space-y-2">
              <Label htmlFor={`assignment-due-${assignment.id}`}>
                Due Date
              </Label>
              <DatePicker
                value={assignment.dueDate}
                onChange={(date) => updateAssignment(assignment.id, { dueDate: date })}
                placeholder="Select due date"
              />
            </div> */}
          </div>
          
          <div className="space-y-2">
            <Label>
              Assignment File
            </Label>
            <Tabs defaultValue="link" className="w-full">
              <TabsList className="grid grid-cols-2 mb-2">
                {/* <TabsTrigger value="upload">Upload File</TabsTrigger> */}
                <TabsTrigger value="link">File Link</TabsTrigger>
              </TabsList>
              
              <TabsContent value="upload" className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={assignment.file ? assignment.file.name : ''}
                    readOnly
                    placeholder="Select an assignment file to upload"
                    className="flex-1"
                  />
                  <input
                    type="file"
                    id={`assignment-file-${assignment.id}`}
                    className="hidden"
                    onChange={(e) => handleFileChange(e, assignment.id)}
                    ref={el => fileInputRefs[assignment.id] = el}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (fileInputRefs[assignment.id]) {
                        fileInputRefs[assignment.id]?.click();
                      }
                    }}
                  >
                    <Upload className="h-4 w-4" />
                  </Button>
                </div>
              </TabsContent>
              
              <TabsContent value="link" className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    id={`assignment-link-${assignment.id}`}
                    value={assignment.link || ''}
                    onChange={(e) => updateAssignment(assignment.id, { 
                      link: e.target.value,
                      file: undefined // Clear file when link is provided
                    })}
                    placeholder="https://example.com/assignment.pdf"
                    className="flex-1"
                  />
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      ))}
      
      {tasks?.length === 0 && (
        <div className="text-center py-8 border rounded-md bg-gray-50">
          <p className="text-gray-500 mb-2">No assignments added yet.</p>
          <Button 
            type="button" 
            variant="outline" 
            onClick={addAssignment}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add First Assignment
          </Button>
        </div>
      )}
    </div>
  );
};

export default AssignmentsList;